import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [isDriverFormOpen, setIsDriverFormOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    setTimeout(() => setShowWhatsApp(true), 1000);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>, formId: string, submitUrl: string) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data: { [key: string]: string } = {};
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        data[key] = value;
      }
    });

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(data).toString(),
      });

      if (response.ok) {
        setFormStatus({ ...formStatus, [formId]: 'success' });
        form.reset();
        setTimeout(() => {
          setFormStatus({ ...formStatus, [formId]: '' });
        }, 5000);
      } else {
        setFormStatus({ ...formStatus, [formId]: 'error' });
      }
    } catch (error) {
      setFormStatus({ ...formStatus, [formId]: 'error' });
    }
  };

  const handleServiceClick = (service: string) => {
    const message = encodeURIComponent(`Hola, estoy interesado en el servicio de ${service}`);
    window.open(`https://wa.me/message/2GTFXGJ74H5AD1?text=${message}`, '_blank');
  };

  const handleDriverFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5a8c6vdcu97hjrn3j5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        setTimeout(() => {
          setIsDriverFormOpen(false);
          setFormStatus('idle');
          form.reset();
        }, 2000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/784069abbb5d0e565eafdc580904c62f/5a64eb61aa29777aaae201bcdd9764aa.png" 
                alt="Mandado Express Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#servicios" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
              }`}>Servicios</a>
              <a href="#conductores" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
              }`}>Para Conductores</a>
              <a href="#cobertura" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
              }`}>Cobertura</a>
              <a href="#nosotros" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-emerald-600' : 'text-white hover:text-emerald-300'
              }`}>Nosotros</a>
            </div>

            <a 
              href="https://wa.me/message/2GTFXGJ74H5AD1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              Solicitar Servicio Ahora
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/b5754672f02602c4ce76ea4862d371cd.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl">
            <h1 className="text-6xl md:text-7xl font-serif font-bold text-white leading-tight mb-6 drop-shadow-2xl" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
              Tu Aliado en Movilidad<br />
              <span className="text-yellow-400 font-serif">y Logística en Risaralda</span>
            </h1>
            <p className="text-xl text-white mb-10 font-light drop-shadow-lg" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
              Conectamos personas, paquetes y oportunidades en todo el Eje Cafetero
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="#servicios" 
                className="bg-emerald-500 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-emerald-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center"
              >
                Enviar Paquete <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a 
                href="#conductores" 
                className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
              >
                Registrarme como Conductor
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-gray-900 mb-2">
              ¿Qué necesitas <strong className="font-bold text-emerald-600">hoy?</strong>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Large Card - Transport */}
            <div 
              onClick={() => handleServiceClick('Transporte Personalizado')}
              className="md:row-span-2 relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow cursor-pointer group h-[600px]"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transform group-hover:scale-105 transition-transform duration-500"
                style={{
                  backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20smiling%20motorcycle%20taxi%20driver%20wearing%20helmet%20in%20urban%20Pereira%20Colombia%20street%20with%20modern%20buildings%20and%20clear%20blue%20sky%20friendly%20service%20provider%20portrait%20with%20simple%20clean%20background&width=800&height=1200&seq=service001&orientation=portrait)'
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-semibold">
                  Transporte Personalizado
                </span>
              </div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-3xl font-bold text-white mb-3">Viaja con Confianza</h3>
                <p className="text-white/90 text-lg">
                  Aeropuerto Matecaña • Terminal • Urbano • Intermunicipal
                </p>
              </div>
            </div>

            {/* Mensajería Card */}
            <div 
              onClick={() => handleServiceClick('Mensajería Express')}
              className="relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow cursor-pointer p-8 bg-gradient-to-br from-emerald-100 to-teal-200 h-[292px] flex flex-col justify-between"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4">
                <i className="ri-mail-send-line text-3xl text-emerald-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Mensajería Express</h3>
                <p className="text-gray-700 text-base">Mandados en minutos. ¿Olvidaste algo? Nosotros lo llevamos.</p>
              </div>
            </div>

            {/* Encomiendas Card */}
            <div 
              onClick={() => handleServiceClick('Encomiendas')}
              className="relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow cursor-pointer p-8 bg-gradient-to-br from-orange-100 to-pink-200 h-[292px] flex flex-col justify-between"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4">
                <i className="ri-box-3-line text-3xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Encomiendas</h3>
                <p className="text-gray-700 text-base">Seguimiento en tiempo real. Tu paquete, nuestra prioridad.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="nosotros" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Mandado Express
              </div>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
                Impulsando la economía local desde el corazón del Eje Cafetero
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10" style={{ lineHeight: '1.8' }}>
                Somos más que una plataforma de transporte y mensajería. Somos el puente que conecta oportunidades, 
                generando empleo flexible para conductores independientes mientras ofrecemos servicios confiables 
                a toda la comunidad de Risaralda.
              </p>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-6xl font-bold text-gray-900 mb-2">2,500+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Conductores Activos</div>
                </div>
                <div>
                  <div className="text-6xl font-bold text-gray-900 mb-2">50,000+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Servicios Completados</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[32px] overflow-hidden shadow-2xl">
                <div 
                  className="h-[500px] bg-cover bg-center"
                  style={{
                    backgroundImage: 'url(https://readdy.ai/api/search-image?query=Diverse%20team%20of%20happy%20delivery%20drivers%20and%20motorcycle%20couriers%20standing%20together%20in%20front%20of%20their%20vehicles%20in%20Pereira%20Colombia%20with%20simple%20clean%20background%20professional%20group%20photo%20showing%20teamwork%20and%20community&width=800&height=1000&seq=team001&orientation=portrait)'
                  }}
                >
                  <div className="absolute top-6 left-6">
                    <span className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center">
                      <i className="ri-map-pin-line mr-2"></i> Pereira, Risaralda
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                    <p className="text-white text-base leading-relaxed">
                      <strong className="font-bold">Nuestra Misión:</strong> Proporcionar soluciones integrales de movilidad 
                      y logística que impulsan el crecimiento económico local.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <span className="inline-block bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Lo que dicen nuestros usuarios
            </span>
            <h2 className="text-5xl font-extrabold text-gray-900 leading-tight">
              Historias reales de<br />nuestra comunidad
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-emerald-200 flex items-center justify-center text-emerald-700 font-bold text-xl">
                  MC
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">María Cardona</div>
                  <div className="text-sm text-gray-500">@mariac_pereira</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "Excelente servicio. Llegué al aeropuerto Matecaña a tiempo gracias a Mandado Express. 
                El conductor fue muy profesional y amable."
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-orange-200 flex items-center justify-center text-orange-700 font-bold text-xl">
                  JR
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Juan Ramírez</div>
                  <div className="text-sm text-gray-500">@juandriver</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "Como conductor, esta plataforma me ha cambiado la vida. Trabajo en mis propios horarios 
                y tengo ingresos constantes. ¡Totalmente recomendado!"
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-xl">
                  LG
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Laura Gómez</div>
                  <div className="text-sm text-gray-500">@laurag_dosq</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "Envié un paquete importante y llegó en perfectas condiciones. El seguimiento en tiempo real 
                me dio mucha tranquilidad."
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 font-bold text-xl">
                  CM
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Carlos Mejía</div>
                  <div className="text-sm text-gray-500">@carlosmejia</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "Necesitaba hacer un mandado urgente y en menos de 30 minutos ya estaba resuelto. 
                Servicio rápido y confiable."
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 font-bold text-xl">
                  AP
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Ana Pérez</div>
                  <div className="text-sm text-gray-500">@anaperez</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "La mejor opción para ir a la Terminal de Transportes. Precios justos y conductores 
                muy respetuosos."
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-4">
                <div className="w-14 h-14 rounded-full bg-indigo-200 flex items-center justify-center text-indigo-700 font-bold text-xl">
                  DR
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Diego Rojas</div>
                  <div className="text-sm text-gray-500">@diegorojas</div>
                </div>
              </div>
              <p className="text-gray-700 mb-4" style={{ lineHeight: '1.6' }}>
                "Como emprendedor, uso Mandado Express para todas mis entregas. Nunca me han fallado. 
                ¡Son los mejores!"
              </p>
              <div className="flex text-yellow-400">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA for Drivers */}
      <section id="conductores" className="py-24 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="rounded-3xl overflow-hidden shadow-2xl mb-12">
            <div 
              className="h-80 bg-cover bg-center relative"
              style={{
                backgroundImage: 'url(https://readdy.ai/api/search-image?query=Happy%20delivery%20driver%20holding%20smartphone%20showing%20mobile%20app%20with%20Colombian%20coffee%20landscape%20background%20with%20mountains%20and%20green%20valleys%20professional%20courier%20smiling%20at%20camera%20with%20simple%20artistic%20background&width=1200&height=600&seq=driver001&orientation=landscape)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
            </div>
          </div>

          <div className="text-center">
            <h2 className="text-4xl leading-relaxed mb-12">
              <span className="font-normal text-gray-600">Sé tu propio jefe.</span><br />
              <span className="italic font-light text-gray-500">Gana en tus tiempos.</span><br />
              <span className="font-bold text-gray-900">Crece con nosotros.</span>
            </h2>

            <a 
              href="#registro" 
              className="inline-block bg-gray-900 text-white px-12 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Registrarme como Conductor
            </a>
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section id="cobertura" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Nuestra Zona de Servicio</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-2xl text-emerald-600"></i>
                  </div>
                  <span className="text-lg text-gray-700 ml-4">Pereira Centro</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-2xl text-emerald-600"></i>
                  </div>
                  <span className="text-lg text-gray-700 ml-4">Dosquebradas</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-2xl text-emerald-600"></i>
                  </div>
                  <span className="text-lg text-gray-700 ml-4">Aeropuerto Matecaña</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-2xl text-emerald-600"></i>
                  </div>
                  <span className="text-lg text-gray-700 ml-4">Terminal de Transportes</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-map-pin-fill text-2xl text-emerald-600"></i>
                  </div>
                  <span className="text-lg text-gray-700 ml-4">Zona Cafetera</span>
                </div>
              </div>
              <div className="mt-8 inline-block bg-emerald-100 text-emerald-800 px-6 py-3 rounded-full font-semibold">
                <i className="ri-time-line mr-2"></i> Cobertura 24/7
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127243.5!2d-75.6967!3d4.8133!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e38874d8c5f8f8f%3A0x8b8b8b8b8b8b8b8b!2sPereira%2C%20Risaralda!5e0!3m2!1ses!2sco!4v1234567890"
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Cobertura Pereira"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Contáctanos</h2>
            <p className="text-lg text-gray-600">Estamos aquí para ayudarte. Escríbenos y te responderemos pronto.</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-10">
            <form 
              id="contact-form" 
              data-readdy-form 
              className="space-y-6"
              onSubmit={(e) => handleFormSubmit(e, 'contact-form', 'https://readdy.ai/api/form/d5a7oifdcu97hjrn3j00')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">Nombre Completo</label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Correo Electrónico</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                  placeholder="+57 300 123 4567"
                />
              </div>

              <div>
                <label htmlFor="asunto" className="block text-sm font-semibold text-gray-700 mb-2">Asunto</label>
                <select
                  id="asunto"
                  name="asunto"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="">Selecciona un asunto</option>
                  <option value="servicio">Solicitar Servicio</option>
                  <option value="conductor">Quiero ser Conductor</option>
                  <option value="soporte">Soporte Técnico</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">Mensaje</label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Cuéntanos cómo podemos ayudarte..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Máximo 500 caracteres</p>
              </div>

              {formStatus['contact-form'] === 'success' && (
                <div className="bg-emerald-100 text-emerald-800 px-4 py-3 rounded-lg">
                  ¡Mensaje enviado exitosamente! Te responderemos pronto.
                </div>
              )}

              {formStatus['contact-form'] === 'error' && (
                <div className="bg-red-100 text-red-800 px-4 py-3 rounded-lg">
                  Hubo un error al enviar el mensaje. Por favor intenta nuevamente.
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-emerald-600 text-white py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Enviar Mensaje
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Preguntas Frecuentes</h2>
            <p className="text-lg text-gray-600">Encuentra respuestas a las dudas más comunes</p>
          </div>

          <div className="space-y-4">
            <FAQItem 
              question="¿Cómo puedo solicitar un servicio?"
              answer="Puedes solicitar nuestros servicios a través de WhatsApp haciendo clic en el botón flotante verde, o contactándonos directamente por teléfono. Pronto tendremos nuestra app móvil disponible."
            />
            <FAQItem 
              question="¿Cuál es el área de cobertura?"
              answer="Operamos en todo Pereira, Dosquebradas y municipios cercanos de Risaralda. Tenemos rutas especiales al Aeropuerto Matecaña y la Terminal de Transportes."
            />
            <FAQItem 
              question="¿Cómo me registro como conductor?"
              answer="Para registrarte como conductor, necesitas tener un vehículo en buen estado (moto o carro), licencia de conducción vigente, y documentos al día. Contáctanos y te guiaremos en el proceso."
            />
            <FAQItem 
              question="¿Cuánto cuesta el servicio?"
              answer="Nuestras tarifas varían según la distancia y tipo de servicio. Contáctanos para obtener una cotización personalizada según tus necesidades específicas."
            />
            <FAQItem 
              question="¿Puedo rastrear mi paquete?"
              answer="Sí, todos nuestros servicios de encomiendas incluyen seguimiento en tiempo real. Recibirás actualizaciones constantes sobre el estado de tu envío."
            />
            <FAQItem 
              question="¿Qué horarios manejan?"
              answer="Ofrecemos servicio 24/7 los 365 días del año. Siempre habrá un conductor disponible para atender tu solicitud."
            />
          </div>
        </div>
      </section>

      {/* PQRS Section */}
      <section id="pqrs" className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">PQRS</h2>
            <p className="text-lg text-gray-600">Peticiones, Quejas, Reclamos y Sugerencias</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-10">
            <form 
              id="pqrs-form" 
              data-readdy-form 
              className="space-y-6"
              onSubmit={(e) => handleFormSubmit(e, 'pqrs-form', 'https://readdy.ai/api/form/d5a7oifdcu97hjrn3j0g')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="pqrs-nombre" className="block text-sm font-semibold text-gray-700 mb-2">Nombre Completo</label>
                  <input
                    type="text"
                    id="pqrs-nombre"
                    name="nombre"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label htmlFor="pqrs-email" className="block text-sm font-semibold text-gray-700 mb-2">Correo Electrónico</label>
                  <input
                    type="email"
                    id="pqrs-email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="pqrs-tipo" className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Solicitud</label>
                <select
                  id="pqrs-tipo"
                  name="tipo"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="">Selecciona el tipo</option>
                  <option value="peticion">Petición</option>
                  <option value="queja">Queja</option>
                  <option value="reclamo">Reclamo</option>
                  <option value="sugerencia">Sugerencia</option>
                </select>
              </div>

              <div>
                <label htmlFor="pqrs-descripcion" className="block text-sm font-semibold text-gray-700 mb-2">Descripción</label>
                <textarea
                  id="pqrs-descripcion"
                  name="descripcion"
                  rows={6}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Describe tu solicitud de manera detallada..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Máximo 500 caracteres</p>
              </div>

              {formStatus['pqrs-form'] === 'success' && (
                <div className="bg-emerald-100 text-emerald-800 px-4 py-3 rounded-lg">
                  ¡Solicitud enviada exitosamente! La revisaremos pronto.
                </div>
              )}

              {formStatus['pqrs-form'] === 'error' && (
                <div className="bg-red-100 text-red-800 px-4 py-3 rounded-lg">
                  Hubo un error al enviar la solicitud. Por favor intenta nuevamente.
                </div>
              )}

              <button
                type="submit"
                className="w-full bg-emerald-600 text-white py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Enviar Solicitud
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Sección Conductores */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Únete a Nuestro Equipo de Conductores
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Gana dinero con tu vehículo. Horarios flexibles, pagos semanales y soporte 24/7.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-600 mr-3 mt-1"></i>
                  <span className="text-lg text-gray-700">Gana hasta $2,000,000 COP mensuales</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-600 mr-3 mt-1"></i>
                  <span className="text-lg text-gray-700">Elige tus propios horarios</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-600 mr-3 mt-1"></i>
                  <span className="text-lg text-gray-700">Pagos semanales garantizados</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-2xl text-emerald-600 mr-3 mt-1"></i>
                  <span className="text-lg text-gray-700">Soporte y capacitación continua</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20delivery%20driver%20standing%20next%20to%20modern%20motorcycle%20with%20delivery%20box%20in%20urban%20Colombian%20city%20setting%2C%20wearing%20safety%20helmet%20and%20uniform%2C%20bright%20daylight%2C%20clean%20simple%20background%2C%20professional%20photography%20style&width=600&height=700&seq=driver-hero-001&orientation=portrait"
                alt="Conductor de Mandado Express"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
            </div>
          </div>
          <div className="mt-12 text-center">
            <button
              onClick={() => setIsDriverFormOpen(true)}
              className="inline-block bg-gray-900 text-white px-12 py-4 rounded-full text-base font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Registrarme como Conductor
            </button>
          </div>
        </div>
      </section>

      {/* Modal de Registro de Conductores */}
      {isDriverFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between rounded-t-3xl">
              <h3 className="text-2xl font-bold text-gray-900">Registro de Conductores</h3>
              <button
                onClick={() => setIsDriverFormOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <form
              id="driver-registration-form"
              data-readdy-form
              onSubmit={handleDriverFormSubmit}
              className="p-8"
            >
              <div className="space-y-6">
                {/* Información Personal */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Información Personal</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        name="nombre_completo"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="Juan Pérez"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cédula *
                      </label>
                      <input
                        type="text"
                        name="cedula"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="1234567890"
                      />
                    </div>
                  </div>
                </div>

                {/* Información de Contacto */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Información de Contacto</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="+57 300 123 4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Correo Electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="correo@ejemplo.com"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dirección de Residencia *
                    </label>
                    <input
                      type="text"
                      name="direccion"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                      placeholder="Calle 123 #45-67, Pereira"
                    />
                  </div>
                </div>

                {/* Información del Vehículo */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Información del Vehículo</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Vehículo *
                      </label>
                      <select
                        name="tipo_vehiculo"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="moto">Moto</option>
                        <option value="carro">Carro</option>
                        <option value="camioneta">Camioneta</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Placa *
                      </label>
                      <input
                        type="text"
                        name="placa"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="ABC123"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Marca *
                      </label>
                      <input
                        type="text"
                        name="marca"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="Honda, Yamaha, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Modelo/Año *
                      </label>
                      <input
                        type="text"
                        name="modelo"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </div>

                {/* Documentación */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Documentación</h4>
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="licencia_conduccion"
                        value="Si"
                        required
                        className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        Tengo licencia de conducción vigente *
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="soat"
                        value="Si"
                        required
                        className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        Mi vehículo tiene SOAT vigente *
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="tecnomecanica"
                        value="Si"
                        required
                        className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
                      />
                      <span className="ml-3 text-sm text-gray-700">
                        Mi vehículo tiene tecnomecánica vigente *
                      </span>
                    </label>
                  </div>
                </div>

                {/* Disponibilidad */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Disponibilidad</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Horario Preferido *
                    </label>
                    <select
                      name="horario_preferido"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="tiempo_completo">Tiempo Completo (8+ horas/día)</option>
                      <option value="medio_tiempo">Medio Tiempo (4-6 horas/día)</option>
                      <option value="fines_semana">Fines de Semana</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>
                </div>

                {/* Experiencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experiencia en Entregas (Opcional)
                  </label>
                  <textarea
                    name="experiencia"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm resize-none"
                    placeholder="Cuéntanos sobre tu experiencia previa en entregas o mensajería..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                {/* Términos */}
                <div>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="acepta_terminos"
                      value="Si"
                      required
                      className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 mt-0.5 cursor-pointer"
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      Acepto los términos y condiciones y autorizo el tratamiento de mis datos personales según la política de privacidad *
                    </span>
                  </label>
                </div>

                {/* Estado del formulario */}
                {formStatus === 'success' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center">
                    <i className="ri-check-line text-2xl text-emerald-600 mr-3"></i>
                    <p className="text-emerald-800 font-medium">
                      ¡Registro enviado exitosamente! Nos pondremos en contacto pronto.
                    </p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center">
                    <i className="ri-error-warning-line text-2xl text-red-600 mr-3"></i>
                    <p className="text-red-800 font-medium">
                      Hubo un error al enviar el formulario. Por favor intenta nuevamente.
                    </p>
                  </div>
                )}

                {/* Botones */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsDriverFormOpen(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? 'Enviando...' : 'Enviar Registro'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white rounded-t-[32px] mx-10 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Newsletter */}
            <div className="md:col-span-1">
              <h3 className="text-3xl font-thin mb-4">Mantente Conectado</h3>
              <p className="text-gray-400 mb-6">Recibe actualizaciones sobre nuevas rutas y promociones</p>
              <form 
                id="newsletter-form" 
                data-readdy-form 
                className="space-y-4"
                onSubmit={(e) => handleFormSubmit(e, 'newsletter-form', 'https://readdy.ai/api/form/d5a7oifdcu97hjrn3j10')}
              >
                <input
                  type="email"
                  name="email"
                  placeholder="Tu correo electrónico"
                  required
                  className="w-full bg-transparent border-b border-gray-600 py-3 text-white placeholder-gray-500 focus:border-emerald-500 outline-none transition-colors"
                />
                {formStatus['newsletter-form'] === 'success' && (
                  <div className="text-emerald-400 text-sm">¡Suscripción exitosa!</div>
                )}
                {formStatus['newsletter-form'] === 'error' && (
                  <div className="text-red-400 text-sm">Error al suscribirse. Intenta nuevamente.</div>
                )}
                <button
                  type="submit"
                  className="bg-white text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors inline-flex items-center whitespace-nowrap cursor-pointer"
                >
                  Suscribirme <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </form>
            </div>

            {/* Navigation */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white">Navegación</h4>
              <ul className="space-y-3">
                <li><a href="#servicios" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Servicios</a></li>
                <li><a href="#conductores" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Para Conductores</a></li>
                <li><a href="#cobertura" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Tarifas</a></li>
                <li><a href="#faq" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Ayuda</a></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-semibold mb-4 text-white">Contacto</h4>
              <ul className="space-y-3 text-gray-400">
                <li>Teléfono: +57 316 492 4439</li>
                <li>Email: mandadoexpresscol@gmail.com</li>
                <li className="pt-4">
                  <strong className="text-white block mb-2">Oficina Principal</strong>
                  Carrera 40 Calle 14 Esquina<br />
                  Manzana 9 Casa 6<br />
                  Pereira, Risaralda
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex space-x-6">
                <a href="https://www.instagram.com/mandadoexpresscol/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Instagram</a>
                <a href="https://www.facebook.com/profile.php?id=61571726168138" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Facebook</a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Twitter</a>
              </div>
              <div className="text-gray-400 text-sm">
                © 2025 Mandado Express. Todos los derechos reservados.
              </div>
              <div className="flex space-x-6 text-sm">
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Términos de Servicio</Link>
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Privacidad</Link>
              </div>
            </div>
            <div className="text-center mt-6">
              <a 
                href="https://readdy.ai/?ref=logo" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-gray-500 hover:text-emerald-400 transition-colors text-sm cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      {showWhatsApp && (
        <a
          href="https://wa.me/message/2GTFXGJ74H5AD1"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all hover:scale-110 z-50 cursor-pointer animate-pulse"
          style={{ animationDuration: '2s' }}
        >
          <i className="ri-whatsapp-line text-3xl text-white"></i>
        </a>
      )}
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <h4 className="text-lg font-semibold text-gray-900 pr-4">{question}</h4>
        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-2xl text-emerald-600 flex-shrink-0`}></i>
      </button>
      {isOpen && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}